(ns minorodata.notifications.api
  (:require
   [clojure.string]
   [re-posh.core :as re-posh]
   [mold-http.core :refer [reg-event-http]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.utils :refer [find-all-by-field find-by-field]]
   [minorodata.notifications.model :refer [->notification ->account-notifications]]))


(defn convert-preferences [preferences]
  (let [all-slack-preferences    (find-all-by-field preferences :target-type "slack")
        any-slack-preference     (some? (first all-slack-preferences))
        all-ms-teams-preferences (find-all-by-field preferences :target-type "ms-teams")
        any-ms-teams-preference  (some? (first all-ms-teams-preferences))]
    {:slack-preference    any-slack-preference
     :ms-teams-preference any-ms-teams-preference}))


(defn fetch-notification-request [_ [{:keys [master-id]}]]
  {:http-xhrio
   {:id     :get-notification-preferences-request
    :method :get
    :url    "/api/notifications/job-preferences"
    :params {:master-id master-id}}})

(defn fetch-notification-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch notification"]}
    {:dispatch-n [[:notification/add-many (map ->notification (:preferences results))]
                  [:account-notifications/add (-> (:account-preferences results)
                                                  (convert-preferences)
                                                  (->account-notifications))]]}))

(reg-event-http
 :notifications.api/fetch-notification
 fetch-notification-request
 fetch-notification-callback)


(defn fetch-all-jobs-notification-request [_ _]
  {:http-xhrio
   {:id     :get-all-jobs-preference
    :method :get
    :url    "/api/notifications/all-jobs-preferences"}})

(defn fetch-all-jobs-notification-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch notification"]}
    {:dispatch [:notification/add-many (map ->notification (:preferences results))]}))

(reg-event-http
 :notifications.api/fetch-all-jobs-notification
 fetch-all-jobs-notification-request
 fetch-all-jobs-notification-callback)


(defn process-notification-preference
  "Process a notification preference for a given target type, determining what kind of
   change needs to be made (create, update, delete) and returning the appropriate preference object.

   Parameters:
   - ps: The accumulated preference list
   - target-type: The type of notification target ('slack', 'ms-teams', or 'email')
   - notification-info: Map containing:
     - notifications: List of existing notifications
     - slack-webhook, ms-teams-webhook, email: New webhook/email values
     - use-account-slack, use-account-ms-teams: Whether to use account webhooks
     - source-type: Source type for the notification
     - master-id: Master ID for the notification source"
  [ps target-type {:keys [notifications slack-webhook ms-teams-webhook email
                          use-account-slack use-account-ms-teams source-type master-id]}]
  (let [target               (case target-type
                               "slack" slack-webhook
                               "ms-teams" ms-teams-webhook
                               "email" email)
        notification         (find-by-field notifications :notification/target-type target-type)
        id                   (:notification/preference-id notification)
        prev-target          (:notification/target notification)
        use-account-url      (or (and (= target-type "slack") use-account-slack)
                                 (and (= target-type "ms-teams") use-account-ms-teams))
        prev-use-account-url (or (and (= target-type "slack") (:notification/use-account-slack notification))
                                 (and (= target-type "ms-teams") (:notification/use-account-ms-teams notification)))
        type                 (cond
                               (and (not id) (or (not-empty target) use-account-url)) "create"
                               (and id (or (not target) (empty? target)) (not use-account-url)) "delete"
                               (and id (or (not= target prev-target) (not= prev-use-account-url use-account-url))) "update")]
    (if (some? type)
      (let [pref (cond-> {:type        type
                          :status      "transition"
                          :target-type target-type
                          :target      (if use-account-url "" target)
                          :source-type source-type
                          :source      master-id}
                   (contains? #{"slack" "ms-teams"} target-type)
                   (assoc :use-account-slack    use-account-slack
                          :use-account-ms-teams use-account-ms-teams)
                   (= target-type "ms-teams")
                   (assoc :extra {:type "workflow-trigger"})
                   (some? id)
                   (assoc :preference-id id))]
        (conj ps pref))
      ps)))


(defn save-notifications-request
  [_ [{:keys [master-id source-type slack-webhook ms-teams-webhook email
              use-account-slack use-account-ms-teams on-success]}]]
  (let [notifications     @(re-posh/subscribe [:notifications.subs/job-notifications-raw master-id])
        notification-info {:notifications        notifications
                           :slack-webhook        slack-webhook
                           :ms-teams-webhook     ms-teams-webhook
                           :email                email
                           :use-account-slack    use-account-slack
                           :use-account-ms-teams use-account-ms-teams
                           :source-type          source-type
                           :master-id            master-id}
        preferences       (reduce (fn [ps target-type]
                                    (process-notification-preference ps target-type notification-info))
                                  []
                                  ["slack" "ms-teams" "email"])]
    (if (seq preferences)
      {:http-xhrio
       {:id        :put-bulk-notification-preferences-request
        :method    :put
        :params    {:preferences preferences}
        :url       "/api/notifications/preferences/bulk"
        :cb-params {:on-success    on-success
                    :master-id     master-id
                    :notifications notifications}}}
      ;; Nothing changed (e.g. every channel was already empty) — mold-http's
      ;; reg-event-http skips all effects when the request fn returns nil, so
      ;; give explicit feedback here rather than silently doing nothing.
      (do
        (when on-success (on-success))
        (dispatch [:app.events/add-snack :success "Notification preferences saved successfully"])
        nil))))

(defn save-notifications-callback [_ [error _ {:keys [on-success master-id notifications]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to save notification preferences"]}
    {:dispatch-n (cond-> (mapv (fn [{:notification/keys [preference-id]}]
                                 [:notification/delete [:notification/preference-id preference-id]])
                               notifications)
                   :always (conj [:notifications.api/fetch-notification {:master-id master-id}])
                   (some? on-success) (conj on-success)
                   :always (conj [:app.events/add-snack :success "Notification preferences saved successfully"]))}))

(reg-event-http
 :notifications.api/save-notifications
 save-notifications-request
 save-notifications-callback)